import React, { FunctionComponent, ReactNode, useMemo } from "react";
import { LucideIcon } from "lucide-react";
import "./ToggleSwitch.scss";

export interface ToggleOption<K extends string = string> {
    key: K;
    label: ReactNode;
    icon?: LucideIcon;
}

interface ToggleSwitchProps<K extends string = string> {
    options: ToggleOption<K>[];
    value: K;
    onChange: (key: K) => void;
    /** acessibilidade */
    ariaLabel?: string;
    className?: string;
}

/**
 * Segmented switch com indicador deslizante (igual ao .choose do balance).
 * Suporta 2-N opcoes. O indicador desliza para a posicao da opcao ativa.
 */
function ToggleSwitch<K extends string>({
    options,
    value,
    onChange,
    ariaLabel,
    className = "",
}: ToggleSwitchProps<K>) {
    const activeIndex = options.findIndex((o) => o.key === value);
    const indicatorStyle = useMemo(() => ({
        width: `calc(${100 / options.length}% - 8px)`,
        left: `calc(${(100 / options.length) * Math.max(activeIndex, 0)}% + 4px)`,
    }), [options.length, activeIndex]);

    return (
        <div className={`ui-toggle-switch ${className}`.trim()} role="tablist" aria-label={ariaLabel}>
            <span className="ui-toggle-indicator" style={indicatorStyle} />
            {options.map((opt) => {
                const Icon = opt.icon;
                const active = opt.key === value;
                return (
                    <button
                        key={opt.key}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        className={`ui-toggle-btn ${active ? "active" : ""}`}
                        onClick={() => onChange(opt.key)}
                        style={{ width: `${100 / options.length}%` }}
                    >
                        {Icon && <Icon size={14} />}
                        <span>{opt.label}</span>
                    </button>
                );
            })}
        </div>
    );
}

const Typed: <K extends string>(p: ToggleSwitchProps<K>) => React.ReactElement = ToggleSwitch;
export default Typed as FunctionComponent<ToggleSwitchProps<any>>;
